import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Heart, Users, Brain, Target, ArrowRight, Star, Sparkles, Shield, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SiteNav, SiteFooter } from './HomePage';

const YAS_LOGO = '/yas-logo.png';
const HERO_IMG = 'https://images.unsplash.com/photo-1758272133833-3a2277d426e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwdXJiYW4lMjB5b3V0aCUyMGdyb3VwJTIwc21pbGluZ3xlbnwwfHx8fDE3ODQ0Mjc5MjF8MA&ixlib=rb-4.1.0&q=85&w=1200';
const MENTOR_IMG = 'https://images.unsplash.com/photo-1758273240360-76b908e7582a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxtZW50b3IlMjBoZWxwaW5nJTIwdGVlbmFnZXJ8ZW58MHx8fHwxNzg0NDI3OTIxfDA&ixlib=rb-4.1.0&q=85&w=800';
const WORKSHOP_IMG = 'https://images.pexels.com/photos/5212697/pexels-photo-5212697.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

const fadeIn = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div style={{ backgroundColor: '#F5F3EF', fontFamily: "'Manrope', sans-serif" }}>
      <SiteNav />

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-6 py-20 md:py-28 text-center">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
          <span className="text-[#E86C3D] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">Our Story</span>
          <h1 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-4xl md:text-6xl font-extrabold tracking-tighter leading-none text-[#2D2824] mb-6">
            Every Young Person<br/>Deserves to Be <span className="text-[#E86C3D]">Known</span>
          </h1>
          <p className="text-lg md:text-xl text-[#2D2824]/60 leading-relaxed max-w-2xl mx-auto">
            Not as a number on a spreadsheet. Not as a name on a sign-in sheet. 
            But as a whole person — with dreams, strengths, challenges, and a future worth investing in.
          </p>
        </motion.div>
      </div>

      {/* The Problem */}
      <div className="bg-white py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeIn} className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#EF4444] text-xs font-bold tracking-[0.2em] uppercase mb-3 block">The Problem We Saw</span>
              <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-3xl md:text-4xl font-extrabold tracking-tight text-[#2D2824] mb-5">
                Youth Programs Were Failing Their Youth
              </h2>
              <div className="space-y-4 text-[#2D2824]/70 leading-relaxed">
                <p>
                  We saw summer employment programs where 500 youth showed up on day one — and by week four, 
                  half of them were invisible. Not because they left, but because nobody had the tools to see them.
                </p>
                <p>
                  We saw lead monitors drowning in the same questions from 20 different youth, answering the same email 
                  20 times, while the one youth who really needed help stayed silent.
                </p>
                <p>
                  We saw program directors spending their weekends manually compiling data into spreadsheets for funders — 
                  data that was already two weeks old by the time it was presented.
                </p>
                <p className="font-bold text-[#2D2824]">
                  We saw organizations with incredible missions being held back by tools from 2005.
                </p>
              </div>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img src={WORKSHOP_IMG} alt="Youth working together" className="w-full h-80 object-cover" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* The Belief */}
      <div style={{ backgroundColor: '#2D2824' }} className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div {...fadeIn}>
            <Heart className="w-12 h-12 mx-auto mb-6 text-[#E86C3D]" />
            <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: 'white' }} className="text-3xl md:text-4xl font-extrabold tracking-tight mb-6">
              We Believe Every Interaction Matters
            </h2>
            <p className="text-lg leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.7)' }}>
              When a youth tells you their mood is "rough" in a weekly check-in — that's not data. That's a human being 
              asking for help in the only way they know how. When they post a win about completing their first day at work — 
              that's not a metric. That's a moment that deserves to be celebrated.
            </p>
            <p className="text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
              YAS Connect was built on the belief that if you pay attention to every one of those moments — 
              and make it impossibly easy to do so — you can transform the way young people experience 
              their development journey.
            </p>
          </motion.div>
        </div>
      </div>

      {/* The Solution */}
      <div className="max-w-5xl mx-auto px-6 py-20">
        <motion.div {...fadeIn} className="text-center mb-14">
          <span className="text-[#4ADE80] text-xs font-bold tracking-[0.2em] uppercase">What We Built</span>
          <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-3xl md:text-4xl font-extrabold tracking-tight text-[#2D2824] mt-3 mb-4">
            A Platform That Actually Listens
          </h2>
        </motion.div>

        <div className="space-y-16">
          {[
            {
              title: 'For the Youth',
              desc: 'We didn\'t build another boring compliance tool. We built something young people actually want to use. A place where they can explore careers with an AI that remembers everything they\'ve told it. Where they can celebrate wins and hype up their friends. Where they can talk — literally speak into their phone — about their goals and dreams instead of filling out forms. We made it feel like their platform, not their program\'s platform.',
              icon: Sparkles, color: '#FFD60A', img: HERO_IMG
            },
            {
              title: 'For the Staff',
              desc: 'We watched lead monitors spend 30 minutes before every meeting trying to remember what each youth was working on. Now they click a name and AI gives them everything — strengths, challenges, mood trends, and exactly what to say. When a youth asks a question, AI drafts a response. When the same question comes up again, one click shares the answer with the whole cohort. We didn\'t just save them time. We gave them superpowers.',
              icon: Shield, color: '#4ADE80', img: MENTOR_IMG
            },
            {
              title: 'For the Organizations',
              desc: 'Every dollar a funder invests in a youth program deserves to be accountable. But you can\'t prove impact with sign-in sheets and end-of-summer surveys. You prove it with real-time data — 87% positive mood this week, 12 career paths explored, 3 goals completed by a youth who started with zero direction. AI writes your executive summary. PDF reports generate in one click. You spend your time leading, not compiling.',
              icon: TrendingUp, color: '#E86C3D', img: null
            },
          ].map((s, i) => (
            <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.1 }}
              className={`grid md:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
              <div className={i % 2 === 1 ? 'md:order-2' : ''}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: s.color + '20' }}>
                    <s.icon className="w-5 h-5" style={{ color: s.color }} />
                  </div>
                  <span className="text-xs font-bold tracking-[0.15em] uppercase" style={{ color: s.color }}>{s.title}</span>
                </div>
                <p className="text-[#2D2824]/70 leading-relaxed text-base">{s.desc}</p>
              </div>
              {s.img ? (
                <div className={`rounded-3xl overflow-hidden shadow-xl ${i % 2 === 1 ? 'md:order-1' : ''}`}>
                  <img src={s.img} alt={s.title} className="w-full h-72 object-cover" />
                </div>
              ) : (
                <div className={`p-12 rounded-3xl bg-[#E86C3D]/5 ${i % 2 === 1 ? 'md:order-1' : ''}`}>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { n: '14', l: 'AI Features' },
                      { n: '8', l: 'Visual Dashboards' },
                      { n: '13', l: 'Courses' },
                      { n: '24/7', l: 'AI Career Advisor' },
                    ].map((stat, j) => (
                      <div key={j} className="text-center p-4 rounded-2xl bg-white">
                        <p style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-2xl font-extrabold text-[#E86C3D]">{stat.n}</p>
                        <p className="text-xs text-[#2D2824]/50">{stat.l}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* The Vision */}
      <div className="bg-white py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div {...fadeIn}>
            <Star className="w-12 h-12 mx-auto mb-5 text-[#FFD60A]" />
            <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-3xl md:text-4xl font-extrabold tracking-tight text-[#2D2824] mb-6">
              Where We're Going
            </h2>
            <div className="space-y-4 text-[#2D2824]/65 leading-relaxed text-base text-left">
              <p>
                We're building toward a future where a young person's development journey doesn't reset every time they move to a new program. 
                Where the career exploration they did in their summer job follows them to their school counselor. 
                Where the skills assessment they took at 16 shows measurable growth when they take it again at 19.
              </p>
              <p>
                We envision a platform where AI doesn't just analyze data — it genuinely understands each young person. 
                Where a youth can talk to their phone about their career dreams and the platform doesn't just transcribe it — 
                it connects those dreams to real pathways, real opportunities, and real people who can help.
              </p>
              <p>
                And we envision a world where every organization that serves young people — from the smallest community nonprofit 
                to the largest city program — has access to the same intelligence tools that used to be reserved for Fortune 500 companies. 
                Because if any group of people deserves cutting-edge technology working for them, it's the next generation.
              </p>
              <p className="font-bold text-[#2D2824] text-center text-lg pt-4">
                That's not just a product vision. That's a promise.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Values */}
      <div className="max-w-5xl mx-auto px-6 py-20">
        <motion.div {...fadeIn} className="text-center mb-12">
          <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-3xl font-extrabold tracking-tight text-[#2D2824]">What Drives Us</h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { title: 'Youth First', desc: 'Every feature is designed from the perspective of a 17-year-old on their first day at a summer job. If it doesn\'t make their life better, we don\'t build it.', color: '#E86C3D' },
            { title: 'Intelligence With Heart', desc: 'AI should feel like a supportive mentor, not a surveillance camera. We use technology to understand and uplift, never to judge or gatekeep.', color: '#8B5CF6' },
            { title: 'Make It Easy', desc: 'If a program director has to watch a training video to use a feature, we failed. Simple is the highest form of sophistication.', color: '#4ADE80' },
            { title: 'Impact Is Everything', desc: 'We measure success by one question: did this young person leave the program knowing themselves better than when they started?', color: '#FFD60A' },
          ].map((v, i) => (
            <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.1 }}
              className="p-6 rounded-3xl bg-white border border-black/5 hover:-translate-y-1 hover:shadow-lg" style={{ transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>
              <div className="w-3 h-10 rounded-full mb-4" style={{ backgroundColor: v.color }} />
              <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-lg font-bold text-[#2D2824] mb-2">{v.title}</h3>
              <p className="text-sm text-[#2D2824]/55 leading-relaxed">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="bg-[#E86C3D] py-20 px-6 text-center">
        <motion.div {...fadeIn}>
          <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: 'white' }} className="text-3xl md:text-4xl font-extrabold tracking-tight mb-4">
            Let's Build Something<br/>Together
          </h2>
          <p className="text-lg mb-8" style={{ color: 'rgba(255,255,255,0.85)' }}>
            Your program. Our platform. Their future.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button onClick={() => navigate('/demo')} className="bg-white text-[#E86C3D] hover:bg-gray-100 rounded-full px-8 py-3 text-lg font-bold shadow-lg" style={{ transition: 'background 0.2s ease' }}>
              Request a Demo <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button onClick={() => navigate('/contact')} className="rounded-full px-8 py-3 text-lg font-bold bg-white/15 text-white hover:bg-white/25 border border-white/30" style={{ transition: 'background 0.2s ease' }}>
              Contact Us
            </Button>
          </div>
        </motion.div>
      </div>

      <SiteFooter />
    </div>
  );
};

export default AboutPage;
